import { useTranslation } from "react-i18next";
import { BreadcrumbJsonLd } from "@/src/common/components/breadcrumb-jsonld";
import { JsonLd } from "@/src/common/components/json-ld";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { env } from "@/src/common/constants/env";
import { DocumentPageShell } from "../components/document-page-shell";
import { LEGAL_INFORMATION } from "../constants/legal";
import { MARKETING_ROUTES } from "../constants/marketing-routes";

function PrivacySection({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<section className="rounded-4xl border border-white/10 bg-white/5 p-6 shadow-[0_24px_70px_-48px_rgba(0,0,0,0.78)] backdrop-blur sm:p-8">
			<h2 className="text-2xl font-semibold tracking-[-0.04em] text-white">{title}</h2>
			<div className="mt-5 space-y-4 text-sm leading-7 text-white/72">{children}</div>
		</section>
	);
}

export default function PrivacyPage() {
	const { t } = useTranslation("marketing");
	const pageUrl = `${env.SITE_URL}${MARKETING_ROUTES.PRIVACY}`;

	return (
		<>
			<PageHelmet title="Privacy Policy" description="Politique de confidentialité de LookDat." />
			<BreadcrumbJsonLd
				items={[
					{ name: t("breadcrumb_home"), url: env.SITE_URL },
					{ name: t("nav_privacy"), url: pageUrl },
				]}
			/>
			<JsonLd
				data={{
					"@context": "https://schema.org",
					"@type": "WebPage",
					name: "Politique de confidentialité",
					url: pageUrl,
					inLanguage: "fr",
					isPartOf: { "@id": `${env.SITE_URL}/#website` },
				}}
			/>
			<DocumentPageShell
				eyebrow="Privacy"
				title="Politique de confidentialité"
				description="Cette politique explique quelles données sont collectées dans le cadre de LookDat, pour quelles finalités, avec qui elles sont partagées et quels sont tes droits."
			>
				<PrivacySection title="1. Responsable du traitement">
					<p>
						Le responsable du traitement des données personnelles traitées dans le cadre de LookDat est{` `}
						<strong>{LEGAL_INFORMATION.publisherName}</strong>, joignable à l'adresse <strong>{LEGAL_INFORMATION.legalEmail}</strong>.
					</p>
				</PrivacySection>

				<PrivacySection title="2. Données que nous collectons">
					<p>Selon ton utilisation du service, nous pouvons traiter les catégories de données suivantes :</p>
					<ul className="list-disc space-y-2 pl-5">
						<li>données de compte, comme l'adresse e-mail et les informations de profil ;</li>
						<li>contenus que tu choisis de publier, comme les photos, vidéos, stories, commentaires et messages ;</li>
						<li>
							données techniques, comme les journaux, identifiants techniques, événements d'application et informations liées à la sécurité
							;
						</li>
						<li>données liées aux notifications, comme les tokens push et préférences e-mail ;</li>
						<li>données envoyées lors des demandes de contact ou de suppression de compte.</li>
					</ul>
				</PrivacySection>

				<PrivacySection title="3. Finalités du traitement">
					<p>Nous utilisons ces données pour :</p>
					<ul className="list-disc space-y-2 pl-5">
						<li>fournir, sécuriser et améliorer le service ;</li>
						<li>permettre la création de groupes privés, la messagerie et le partage de contenus ;</li>
						<li>gérer l'authentification, les invitations, les notifications et la modération ;</li>
						<li>prévenir les abus, le spam et les usages frauduleux ;</li>
						<li>répondre aux demandes d'assistance, de contact ou de suppression.</li>
					</ul>
				</PrivacySection>

				<PrivacySection title="4. Bases légales">
					<p>Les traitements sont réalisés sur la base :</p>
					<ul className="list-disc space-y-2 pl-5">
						<li>de l'exécution du contrat lorsque les traitements sont nécessaires au fonctionnement du service ;</li>
						<li>
							de notre intérêt légitime pour la sécurité, la prévention des abus, l'amélioration du produit et la gestion de la relation
							utilisateur ;
						</li>
						<li>de ton consentement lorsque celui-ci est requis, notamment pour certains traceurs ou certaines communications ;</li>
						<li>de nos obligations légales lorsque la loi l'impose.</li>
					</ul>
				</PrivacySection>

				<PrivacySection title="5. Destinataires et sous-traitants">
					<p>Les données peuvent être traitées par des prestataires intervenant pour l'exploitation du service, notamment :</p>
					<ul className="list-disc space-y-2 pl-5">
						<li>Cloudflare pour l'hébergement, les services réseau et l'infrastructure ;</li>
						<li>Plunk pour l'envoi d'e-mails transactionnels ;</li>
						<li>Expo pour certains services liés à l'application mobile ;</li>
						<li>Google Sign-In et Apple Sign-In pour l'authentification lorsque tu choisis ces méthodes.</li>
					</ul>
					<p>
						Ces traitements peuvent impliquer des transferts de données hors de l'Union européenne, encadrés par les mécanismes juridiques
						appropriés lorsqu'ils s'appliquent.
					</p>
				</PrivacySection>

				<PrivacySection title="6. Conservation des données">
					<p>
						Nous conservons les données aussi longtemps que nécessaire pour fournir le service, respecter nos obligations légales, résoudre
						les litiges et garantir la sécurité de la plateforme.
					</p>
					<p>
						En cas de demande de suppression de compte, la suppression peut être différée pendant une période de <strong>15 jours</strong>{" "}
						afin de permettre l'annulation de la demande.
					</p>
				</PrivacySection>

				<PrivacySection title="7. Tes droits">
					<p>
						Selon la réglementation applicable, tu peux demander l'accès, la rectification, l'effacement, la limitation, l'opposition ou la
						portabilité de tes données.
					</p>
					<p>
						Pour exercer ces droits, contacte-nous à <strong>{LEGAL_INFORMATION.legalEmail}</strong>.
					</p>
					<p>
						Si tu estimes que tes droits ne sont pas respectés, tu peux également introduire une réclamation auprès d'une autorité de
						contrôle compétente, notamment en France auprès de la CNIL.
					</p>
				</PrivacySection>

				<PrivacySection title="8. Sécurité">
					<p>
						Nous mettons en place des mesures techniques et organisationnelles destinées à protéger les données personnelles contre les
						accès non autorisés, l'altération, la divulgation ou la destruction.
					</p>
					<p>Cela inclut notamment des mécanismes de protection d'accès, de modération, de journalisation et de limitation des abus.</p>
				</PrivacySection>

				<PrivacySection title="9. Contact">
					<p>
						Pour toute question relative à la présente politique de confidentialité, écris-nous à{" "}
						<strong>{LEGAL_INFORMATION.legalEmail}</strong>.
					</p>
				</PrivacySection>
			</DocumentPageShell>
		</>
	);
}
